/**
 * THE COMPOSE SEQUENCE — five screens, as pure functions.
 *
 * Function for function off `helpers/broadcast-compose.js`, which is the one place in this
 * product that already gets a long, high-stakes, multi-decision job right, and which is
 * used for exactly one feature and not for the task builder. Same names, same shapes, same
 * two properties — because the two properties are the whole reason it works:
 *
 *  1. **A step you have not earned is not a step you can be on.** `resolveStep` takes the
 *     step the URL asked for and hands back the first one that is not yet finished, so
 *     `?step=check` typed into the address bar on an empty draft lands on "What it does",
 *     not on a button that turns a half-written task on for live customers.
 *  2. **A step that has nothing to ask is not shown.** A task in which nothing can fail has
 *     no "What if" screen, in the rail, in the guard and in the Next/Back arithmetic — a
 *     screen that renders an empty list and a Next button teaches an owner that the
 *     sequence is padding.
 *
 * Every function is total and side-effect free, so a spec can walk the whole sequence —
 * including the states that are unreachable by clicking — without mounting anything.
 *
 * ── THE ONE GATE ──────────────────────────────────────────────────────────────────────
 * `isStepComplete` IS `localProblems(spineToDefinition(draft))`, filtered to the screen
 * that owns each problem. It is not a second reading of the same rules, and that is not
 * tidiness: a second spelling drifts, and the drift shows up as a composer that lets an
 * owner walk to the end and then watches the server refuse the save — or, worse, one that
 * blocks a step the server would have taken.
 *
 * `localProblems` is itself the client mirror of `taskDefinitionSchema` and `validate.ts`,
 * so there are two spellings in the world and not three, and the one that is load bearing
 * is still the server's.
 *
 * TWO RULES ARE NOT FROM THERE, and they are named here rather than hidden: a task's own
 * TITLE and WHEN IT STARTS are not part of a `TaskDefinition` at all — they are columns on
 * the task row — so no definition check could ever report them. They are written once, in
 * `titleAndTriggerProblems`, in the same `{stepId, problem, detail}` shape everything else
 * arrives in, so there is still exactly one list and one emptiness test.
 *
 * ── THE PARTITION IS TOTAL, AND THAT IS LOAD BEARING ──────────────────────────────────
 * Every problem is attributed to a screen that is actually being shown. A problem owned by
 * a screen this draft skips is re-attributed to `steps`, which is never skipped. Without
 * that, a stored task carrying — say — a `clears` entry naming an answer nothing writes,
 * on a task with no jumps in it, would belong to a "What if" screen that is not in the
 * sequence: every shown screen would report itself finished while the definition is one the
 * server refuses. `specs/task-compose.spec.js` asserts the union over the shown screens is
 * the whole list.
 */
import {
  definitionToSpine,
  spineJumps,
  spineToDefinition,
} from './task-spine.js';
import { localProblems } from './task-flow-draft.js';
import { sameGraph } from './task-spine-edit.js';
import { isOneLanguage } from './missing-language.js';
import { MAX_CTA } from './task-arabic.js';
import { withoutSteps } from './form-layout.js';

/** In order. The rail draws this, the guard walks it, Next/Back index into it. */
export const TASK_STEPS = ['job', 'steps', 'whatif', 'arabic', 'check'];

/** `title` and `trigger` on the task row. The BFF's own limits, restated — `routes/api/scenarios.ts`. */
export const MAX_TITLE = 200;
export const MAX_TRIGGER = 500;
/** P-2.7: "First thing to say", per language (`schema.ts` `MAX_OPENING`). */
export const MAX_OPENING = 300;
/** P-10.9: a `words` task's start words (`schema.ts` `MAX_START_WORDS`, `MAX_START_WORD`). */
export const MAX_START_WORDS = 10;
export const MAX_START_WORD = 40;
/** P-10.9: how a task starts. `assistant` is today's, and the default. */
export const START_MODES = Object.freeze(['assistant', 'words', 'menu']);

/**
 * A brand-new task: no rows at all, AND SWITCHED OFF.
 *
 * NOT one empty row, which is what the free-text form opens with. There the empty box was
 * the teaching — "a task is a list of steps" — and here the list is the screen's whole
 * subject and a starter usually fills it. An empty list is also honestly incomplete: the
 * graph it compiles to has no first step, `localProblems` says so, and the Steps screen is
 * correctly unfinished from the first frame rather than by a rule written here.
 *
 * ── AND `enabled: false`, WHICH IS THE WHOLE POINT ────────────────────────────────────
 * A blind reader, given nothing but the screen, found the one contradiction every builder
 * and every design judge walked past: "I am on step 1 of 5, so the thing is already
 * answering my customers while I am still halfway through writing it. I would be scared to
 * touch this page at all during shop hours."
 *
 * He was reading a true sentence. `tenant_scenarios.enabled` is `NOT NULL DEFAULT true`, so
 * a task created without an opinion goes live the instant it exists — and a five-screen
 * path that ends in "Check it" promises the opposite. The default was the lie, not the copy.
 *
 * NOTHING ABOUT THE SERVER HAD TO CHANGE for this: the column default only applies when
 * nobody says otherwise, and the whole chain already carries an explicit `false` —
 * `api/client.js createTask` documents `enabled?: boolean`, the BFF writes
 * `enabled: patch.enabled ?? true` on POST, and the sidecar's `scenarioBody` reads
 * `z.boolean().default(true)`, which honours an explicit `false` and defaults only an
 * absent one. So this line is the fix, and the fix is one word.
 *
 * A task is switched on on the LAST screen, after the rehearsal, which is what that screen
 * is for. See `CheckScreen.vue`.
 */
export const blankDraft = () => ({
  title: '',
  trigger: '',
  enabled: false,
  spine: { rows: [] },
  // The form's submit button, per language (`ctaEn`/`ctaAr`, P-7.1). Task-wide, so it lives
  // on the draft rather than on a step, and `definitionOf` writes it into the graph.
  cta: { en: '', ar: '' },
  // P-2.7: "First thing to say", per language. Task-wide, written into the graph like `cta`.
  opening: { en: '', ar: '' },
  // P-10.9: how it starts. Written into the graph (`start`) only when it is not `assistant`.
  start: { mode: 'assistant', words: [] },
  // P-10.26: the channels it runs on; `null` is every channel. A column of the task row.
  channels: null,
  // P-4.10: a NEW task's WhatsApp form is split into pages for her. Written into the graph.
  form: { mode: 'auto' },
});

/** P-10.9: how a stored graph starts, as the draft holds it. Anything unknown is the assistant. */
export function startOf(definition) {
  const start = definition?.start;
  const mode = START_MODES.includes(start?.mode) ? start.mode : 'assistant';
  const words =
    mode === 'words' && Array.isArray(start?.words)
      ? start.words.filter(word => typeof word === 'string')
      : [];
  return { mode, words };
}

/**
 * A saved task as the composer edits it, plus whether the list could show it at all.
 *
 * `unreadable` is not "this task is broken" — it means the ordered list has nowhere to put
 * this graph's shape, and the map is the honest answer. The draft still comes back with
 * whatever rows there are (none, in that case), so a caller that renders the map does not
 * also have to carry a null.
 */
export const draftFromTask = task => {
  const read = definitionToSpine(task?.definition);
  return {
    draft: {
      title: typeof task?.title === 'string' ? task.title : '',
      trigger: typeof task?.trigger === 'string' ? task.trigger : '',
      enabled: task?.enabled !== false,
      spine: read.spine ?? { rows: [] },
      cta: {
        en:
          typeof task?.definition?.ctaEn === 'string'
            ? task.definition.ctaEn
            : '',
        ar:
          typeof task?.definition?.ctaAr === 'string'
            ? task.definition.ctaAr
            : '',
      },
      opening: {
        en:
          typeof task?.definition?.openingEn === 'string'
            ? task.definition.openingEn
            : '',
        ar:
          typeof task?.definition?.openingAr === 'string'
            ? task.definition.openingAr
            : '',
      },
      start: startOf(task?.definition),
      channels: Array.isArray(task?.channels) ? [...task.channels] : null,
      // P-4.10: carried exactly as stored, so a save from here never wipes the layout she
      // arranged on the Arrange form page. `null`: this task never had one (today's single page).
      form:
        task?.definition?.form &&
        typeof task.definition.form === 'object' &&
        !Array.isArray(task.definition.form)
          ? task.definition.form
          : null,
    },
    unreadable: read.unreadable,
    reasons: read.reasons,
  };
};

/**
 * The graph this draft saves: the spine's steps, and the form's button when one is written.
 *
 * A blank button is LEFT OUT rather than sent as `''`: a stored graph without `ctaEn` must
 * compare equal to a draft that never touched the box, or opening a task would read as an
 * edit. Before P-7.1 this composer dropped the two keys on every save; they now round-trip.
 */
export const definitionOf = draft => {
  const definition = spineToDefinition(draft?.spine);
  const en = (draft?.cta?.en ?? '').trim();
  const ar = (draft?.cta?.ar ?? '').trim();
  // P-2.7: her line breaks are hers; only the ends are trimmed, as the sidecar reads it.
  const openingEn = (draft?.opening?.en ?? '').trim();
  const openingAr = (draft?.opening?.ar ?? '').trim();
  const start = draft?.start ?? { mode: 'assistant' };
  // P-4.10: the form block, without any step this draft deleted (design 4). The same object when
  // nothing was deleted, so an untouched task compares equal to its saved graph.
  const form = draft?.form
    ? withoutSteps(
        draft.form,
        (definition.steps ?? []).map(step => step.id)
      )
    : null;
  return {
    ...definition,
    ...(form ? { form } : {}),
    ...(en === '' ? {} : { ctaEn: en }),
    ...(ar === '' ? {} : { ctaAr: ar }),
    ...(openingEn === '' ? {} : { openingEn }),
    ...(openingAr === '' ? {} : { openingAr }),
    // P-10.9: absent is the assistant, so a task that never chose compares equal to its graph.
    ...(start.mode === 'words'
      ? {
          start: {
            mode: 'words',
            words: (start.words ?? [])
              .map(word => String(word).trim())
              .filter(Boolean),
          },
        }
      : {}),
    ...(start.mode === 'menu' ? { start: { mode: 'menu' } } : {}),
  };
};

/**
 * P-5.8: is this draft simply the saved task? Title and trigger trimmed as the server stores
 * them, the switch, and the graph compared with `sameGraph` (key order is not a change).
 * `saved` is the page's baseline (`savedWire`), whose `definition` is the ROUND-TRIPPED saved
 * graph, so a task saved by an older build compares equal to what the builder makes of it.
 */
export const draftMatchesSaved = (draft, saved) =>
  Boolean(saved) &&
  String(draft?.title ?? '').trim() === saved.title &&
  String(draft?.trigger ?? '').trim() === saved.trigger &&
  (draft?.enabled !== false) === saved.enabled &&
  sameGraph(definitionOf(draft), saved.definition);

// ── what this draft holds ─────────────────────────────────────────────────────────────

const rowsOf = draft =>
  Array.isArray(draft?.spine?.rows) ? draft.spine.rows : [];

const typesIn = draft =>
  rowsOf(draft)
    .map(row => (row && typeof row.step === 'object' ? row.step?.type : null))
    .filter(Boolean);

/**
 * Is there anything in this task that can go wrong in a way the OWNER chooses the answer to?
 *
 * Three shapes, and nothing else: a live list can come back empty, an action can fail, and
 * a jump is a way out of the straight line that the list itself did not write. A task made
 * of questions and a message has no failure an owner can be asked about, and asking her
 * anyway is how a sequence teaches that its screens are decoration.
 *
 * Deliberately NOT here: a customer who will not answer, and a turn the assistant could not
 * read. `types.ts:216` and `:218-222` — three refusals on one step, or two unreadable turns
 * in a row, hand the chat to a person. That is engine behaviour, and it is a reassurance to
 * print on the screen, never a control. An invented branch on the headline screen is worse
 * than no screen.
 */
const canFail = draft =>
  typesIn(draft).some(type => type === 'choose_live' || type === 'call') ||
  spineJumps(draft?.spine).length > 0;

/**
 * Is there a box on this task that holds the SAME words in two languages?
 *
 * Those words go on a WhatsApp card, and a card has nobody to phrase anything — they are
 * sent exactly as typed. Three shapes carry such a box and no others:
 *
 *   • `ask` and `choose`, which carry `questionEn`/`questionAr` (`schema.ts:84-107`), and a
 *     `choose`'s option labels on top;
 *   • a `tell` written in the owner's exact words, which carries `exactEn`/`exactAr`.
 *
 * A `choose_live` carries NEITHER, by the schema's own deliberate omission, so a task made
 * only of live lists, actions and hand-overs has nothing to write in Arabic and is not
 * asked. THE DESIGN SAYS "no step can be a form field" AND NAMES THOSE THREE TYPES; the
 * exact-words `tell` is the case that sentence does not cover, and skipping the screen for
 * a task whose one message is written in exact words would lose that message's Arabic with
 * nothing on screen to say so. So the test is "has a bilingual box", which is the same
 * answer for every task the sentence does describe.
 */
const hasBilingualBox = draft =>
  rowsOf(draft).some(row => {
    const step = row && typeof row === 'object' ? row.step : null;
    if (!step || typeof step !== 'object') return false;
    if (step.type === 'ask' || step.type === 'choose') return true;
    return step.type === 'tell' && step.exactEn !== undefined;
  });

/**
 * Which screens this draft actually has.
 *
 * `job`, `steps` and `check` are always in it: a task always has a name and a trigger,
 * always has an order, and is always turned on from one place.
 */
export const stepsFor = (draft, ctx = {}) => {
  const steps = ['job', 'steps'];
  if (canFail(draft)) steps.push('whatif');
  // P-7.1: an account whose customers write in one language has nothing to write in the
  // other, so the words-on-the-card screen is not in its sequence.
  if (hasBilingualBox(draft) && !isOneLanguage(ctx.customerLanguages)) {
    steps.push('arabic');
  }
  steps.push('check');
  return steps;
};

// ── the gate ──────────────────────────────────────────────────────────────────────────

/**
 * The two rules a `TaskDefinition` has nowhere to put, in the shape every other problem
 * arrives in so there is one list and one emptiness test.
 *
 * `stepId: ''` because they belong to the task, not to a step — the same empty id
 * `problemsByStep` already groups page-level problems under.
 */
export const titleAndTriggerProblems = draft => {
  const problems = [];
  const title = (draft?.title ?? '').trim();
  const trigger = (draft?.trigger ?? '').trim();
  if (title === '') problems.push({ stepId: '', problem: 'no_title' });
  else if (title.length > MAX_TITLE) {
    problems.push({ stepId: '', problem: 'title_long' });
  }
  const mode = draft?.start?.mode ?? 'assistant';
  // P-10.9: only a task the assistant starts is told when; the others store ''.
  if (trigger === '' && mode === 'assistant') {
    problems.push({ stepId: '', problem: 'no_trigger' });
  } else if (trigger.length > MAX_TRIGGER) {
    problems.push({ stepId: '', problem: 'trigger_long' });
  }
  if (mode === 'words') {
    const words = (draft?.start?.words ?? [])
      .map(word => String(word).trim())
      .filter(Boolean);
    if (words.length === 0) {
      problems.push({ stepId: '', problem: 'no_start_words' });
    } else if (words.length > MAX_START_WORDS) {
      problems.push({ stepId: '', problem: 'start_words_many' });
    } else if (words.some(word => word.length > MAX_START_WORD)) {
      problems.push({ stepId: '', problem: 'start_word_long' });
    }
  }
  // P-2.7: the opening line's own cap, per language.
  if (
    (draft?.opening?.en ?? '').trim().length > MAX_OPENING ||
    (draft?.opening?.ar ?? '').trim().length > MAX_OPENING
  ) {
    problems.push({ stepId: '', problem: 'opening_long' });
  }
  // P-10.26: limited to no channel at all would be a task that can never start.
  if (Array.isArray(draft?.channels) && draft.channels.length === 0) {
    problems.push({ stepId: '', problem: 'no_channels' });
  }
  // The form's button (`schema.ts` `MAX_CTA`): refused by the server's schema, so held here.
  if (
    (draft?.cta?.en ?? '').trim().length > MAX_CTA ||
    (draft?.cta?.ar ?? '').trim().length > MAX_CTA
  ) {
    problems.push({ stepId: '', problem: 'cta_long' });
  }
  return problems;
};

/**
 * Everything this draft still gets wrong — the task's own two fields, then every rule the
 * server would refuse the graph for.
 *
 * The definition is compiled here and nowhere else in this file, so the screens and the
 * gate can never be looking at different graphs.
 *
 * `ctx.changeConnections` (a Set of connection names) adds P-3.4's confirm rule; without it
 * the graph is judged as before.
 */
export const composeProblems = (draft, ctx = {}) => [
  ...titleAndTriggerProblems(draft),
  ...localProblems(spineToDefinition(draft?.spine), ctx),
];

/**
 * Which screen answers which problem.
 *
 * Anything not named here is the spine's — a step's own shape, its links, its options, its
 * save key, the order — and the Steps screen is where every one of those is fixed. Naming
 * the exceptions rather than the rule is what keeps a problem kind added by a newer sidecar
 * from falling out of the sequence entirely: an unknown kind lands on a screen that is
 * always shown, and blocks, which is the safe direction.
 */
const OWNER = {
  // The task row's own columns.
  no_title: 'job',
  title_long: 'job',
  no_trigger: 'job',
  trigger_long: 'job',
  // P-10.9, P-2.7 and P-10.26: how it starts, what it says first and where it runs.
  no_start_words: 'job',
  start_words_many: 'job',
  start_word_long: 'job',
  trigger_missing: 'job',
  opening_long: 'job',
  no_channels: 'job',
  // The form's button is written on the Check screen (P-7.1).
  cta_long: 'check',
  // The failure arms and the jumps — every one of them is an answer to "what if…".
  no_if_none: 'whatif',
  dangling: 'whatif',
  unknown_clear: 'whatif',
};

/**
 * The screen that owns a problem kind, given the screens this draft is showing.
 *
 * The second clause is the total-partition guarantee: a screen this draft skips cannot own
 * anything, because nothing on a skipped screen is ever read, so the problem would be
 * reported to nobody and the sequence would let the owner walk past it.
 *
 * One spelling, used by both the exported reader and the filter below.
 */
const ownerOf = (kind, shown) => {
  const owner = OWNER[kind] ?? 'steps';
  return shown.includes(owner) ? owner : 'steps';
};

/** The screen that owns a problem kind on THIS draft. */
export const stepOwning = (kind, draft, ctx = {}) =>
  ownerOf(kind, stepsFor(draft, ctx));

/** Only the problems this screen answers. Total; an unknown screen name owns nothing. */
export const problemsForStep = (step, draft, ctx = {}) => {
  const shown = stepsFor(draft, ctx);
  // Only the account's languages ride along: the connection rules stay the Check screen's.
  return composeProblems(draft, {
    customerLanguages: ctx.customerLanguages,
  }).filter(problem => ownerOf(problem.problem, shown) === step);
};

/**
 * Is this one screen finished?
 *
 * `check` is never "complete" — it is the screen the write happens on, so there is nothing
 * after it to unlock and nothing here to say yes to. It is also the last screen, so nothing
 * in `resolveStep` ever asks.
 */
export const isStepComplete = (step, draft, ctx = {}) => {
  if (step === 'check') return false;
  return problemsForStep(step, draft, ctx).length === 0;
};

/**
 * The screen the page may actually render, given the one the URL asked for.
 *
 * Walks forward from the first and stops at the first unfinished one, so a deep link can
 * only ever land EARLIER than it asked, never later. An unknown name is treated as "the
 * furthest you have earned" rather than as an error — a bookmark from before a screen was
 * renamed should open the sequence, not a dead end.
 *
 * THE PROPERTY THAT MATTERS ON THE FORTIETH EDIT: a draft with no problems left never
 * bounces. A saved task is exactly such a draft — it passed this same gate on the way in —
 * so opening one and asking for any of its screens lands on that screen. It is asserted
 * directly, against the real sixteen-step booking task running on dev.
 */
export const resolveStep = (requested, draft, ctx = {}) => {
  const steps = stepsFor(draft, ctx);
  const wantedAt = steps.indexOf(requested);
  const target = wantedAt === -1 ? steps.length - 1 : wantedAt;
  for (let at = 0; at < target; at += 1) {
    if (!isStepComplete(steps[at], draft, ctx)) return steps[at];
  }
  return steps[target];
};

/** The next / previous screen, or null at either end. */
export const stepAfter = (step, draft, ctx = {}) => {
  const steps = stepsFor(draft, ctx);
  const at = steps.indexOf(step);
  return at === -1 || at === steps.length - 1 ? null : steps[at + 1];
};

export const stepBefore = (step, draft, ctx = {}) => {
  const steps = stepsFor(draft, ctx);
  const at = steps.indexOf(step);
  return at <= 0 ? null : steps[at - 1];
};

export default TASK_STEPS;
